import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useAuthStore } from "../stores/authStore";
import { isEnterpriseSubdomain, appSubdomainUrl } from "./subdomain";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"] as const;

/**
 * Forces a logout after `timeoutMs` of no user activity.
 * On enterprise subdomains, redirects to app subdomain login after logout.
 */
export function useIdleTimeout(timeoutMs: number) {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleIdle() {
      clear();
      const loginUrl = isEnterpriseSubdomain()
        ? appSubdomainUrl("/login?reason=idle")
        : "/login?reason=idle";
      if (loginUrl.startsWith("http")) {
        window.location.href = loginUrl;
      } else {
        navigate(loginUrl);
      }
    }

    function resetTimer() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleIdle, timeoutMs);
    }

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [timeoutMs]);
}
